// Field-report presentation metadata (docs/19 §1). Single source of truth for
// how each report type + priority is styled and how its condensed payload
// (§1.4) is rendered into label/value rows.

import type { ReportType, ReportPriority } from '../store/reportStore';

export const REPORT_TYPE_META: Record<ReportType, { label: string; icon: string }> = {
  CONTACTREP: { label: 'Contact', icon: '💥' },
  SITREP: { label: 'Situation', icon: '🗒️' },
  SPOTREP: { label: 'Spot', icon: '👁️' },
  CASEVAC: { label: 'Casualty Evac', icon: '🚑' },
  LOCSTAT: { label: 'Loc/Status', icon: '📍' },
  LOGREQ: { label: 'Logistics Req', icon: '📦' },
  OBSTACLE: { label: 'Obstacle', icon: '🚧' },
  FREETEXT: { label: 'Free Text', icon: '💬' },
};

export const REPORT_TYPES = Object.keys(REPORT_TYPE_META) as ReportType[];

// Priority → command-theme styling. FLASH/IMMEDIATE are the ack-demanding tiers.
export const PRIORITY_META: Record<
  ReportPriority,
  { label: string; badge: string; border: string; dot: string; rank: number }
> = {
  FLASH: {
    label: 'FLASH',
    badge: 'bg-severity-critical/20 text-severity-critical border border-severity-critical/40',
    border: 'border-severity-critical/60',
    dot: 'bg-severity-critical',
    rank: 0,
  },
  IMMEDIATE: {
    label: 'IMMEDIATE',
    badge: 'bg-severity-high/20 text-severity-high border border-severity-high/40',
    border: 'border-severity-high/50',
    dot: 'bg-severity-high',
    rank: 1,
  },
  PRIORITY: {
    label: 'PRIORITY',
    badge: 'bg-severity-medium/20 text-severity-medium border border-severity-medium/40',
    border: 'border-command-border',
    dot: 'bg-severity-medium',
    rank: 2,
  },
  ROUTINE: {
    label: 'ROUTINE',
    badge: 'bg-command-border/60 text-gray-400 border border-command-border',
    border: 'border-command-border',
    dot: 'bg-gray-500',
    rank: 3,
  },
};

export const REPORT_PRIORITIES = Object.keys(PRIORITY_META) as ReportPriority[];
export const REPORT_STATUSES = ['RECEIVED', 'ACKED'] as const;

export interface PayloadRow {
  label: string;
  value: string;
}

function str(v: unknown): string {
  if (v === null || v === undefined || v === '') return '';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  return String(v);
}

// Turn a typed payload (§1.4) into ordered label/value rows for display.
export function payloadRows(type: ReportType, payload: Record<string, unknown> | null): PayloadRow[] {
  if (!payload) return [];
  const rows: PayloadRow[] = [];
  const add = (label: string, value: unknown) => {
    const s = str(value);
    if (s) rows.push({ label, value: s });
  };

  switch (type) {
    case 'CONTACTREP':
      add('Direction', payload.direction);
      add('Enemy Strength', payload.enemyStrength);
      add('Activity', payload.activity);
      add('Own Status', payload.ownStatus);
      break;
    case 'SITREP':
      add('Situation', payload.situation);
      add('Intention', payload.intention);
      break;
    case 'SPOTREP': {
      add('Size', payload.size);
      add('Activity', payload.activity);
      const loc = payload.location as { lat?: number; lon?: number } | undefined;
      if (loc && typeof loc.lat === 'number' && typeof loc.lon === 'number') {
        add('Location', `${loc.lat.toFixed(5)}, ${loc.lon.toFixed(5)}`);
      }
      add('Unit', payload.unit);
      add('Time', payload.time);
      add('Equipment', payload.equipment);
      break;
    }
    case 'CASEVAC':
      add('Casualties', payload.casualties);
      add('Precedence', payload.precedence);
      add('Marking', payload.marking);
      add('Security at Pickup', payload.securityAtPickup);
      break;
    case 'LOCSTAT':
      add('Strength', payload.strength);
      add('Posture', payload.posture);
      break;
    case 'LOGREQ': {
      const items = Array.isArray(payload.items) ? (payload.items as Array<Record<string, unknown>>) : [];
      items.forEach((it, i) => {
        const parts = [str(it.kind), str(it.qty) && `× ${str(it.qty)}`, str(it.note)].filter(Boolean);
        add(`Item ${i + 1}`, parts.join('  '));
      });
      break;
    }
    case 'OBSTACLE':
      add('Kind', payload.kind);
      add('Passable', payload.passable);
      add('Note', payload.note);
      break;
    case 'FREETEXT':
      add('Message', payload.text);
      break;
    default:
      Object.entries(payload).forEach(([k, v]) => add(k, v));
  }
  return rows;
}

// Minutes elapsed since a report was received (drives the SLA timer chip).
export function minutesSince(iso: string): number {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return 0;
  return Math.max(0, Math.floor((Date.now() - t) / 60000));
}
